/**
 * Dispatch a :type:`ContentBlock` to the right view component.
 *
 * The router is block-type-agnostic in the turn-list renderer: it
 * takes a block plus whatever context it needs (streaming flag,
 * partial tool JSON, paired tool result, live subagent state) and
 * hands off. This keeps ``TurnStream`` free of a giant switch
 * statement.
 *
 * Vertical rhythm between blocks lives in :class:`AssistantStream`,
 * not here — block components render pure content and the parent
 * wraps each one with the right top margin.
 */

import type { ActiveTask, BlockTiming, SubagentState } from '@/lib/agent/reducer';
import type { ContentBlock, ToolResultBlock } from '@/lib/agent/types';
import { CodeExecutionResultView } from './CodeExecutionResultView';
import { DocumentBlockView } from './DocumentBlockView';
import { ImageBlockView } from './ImageBlockView';
import { TextBlockView } from './TextBlockView';
import { ThinkingBlockView } from './ThinkingBlockView';
import { ToolUseBlockView } from './ToolUseBlockView';
import { WebSearchResultView } from './WebSearchResultView';

export interface BlockRouterProps {
  block: ContentBlock;
  streaming?: boolean;
  partialJson?: string;
  /** Per-block start/stop timing, when the reducer captured one. */
  timing?: BlockTiming;
  /** Looked up by tool_use_id for ToolUseBlock / ServerToolUseBlock rendering. */
  getToolResult?: (toolUseId: string) => ToolResultBlock | undefined;
  /** Looked up by tool_use_id for ``Task`` tool calls that spawned a subagent. */
  getSubagent?: (toolUseId: string) => SubagentState | undefined;
  /** Looked up by tool_use_id for tools that spawned SDK background tasks. */
  getActiveTask?: (toolUseId: string) => ActiveTask | undefined;
  /** Live pending state for a tool row, derived from reducer activity rather than transcript shape. */
  getToolPending?: (toolUseId: string) => boolean;
  /** Current agent session id, used by inline background task tails. */
  sessionId?: string | null;
  /** Stop a live background task from its inline tail. */
  onStopTask?: (taskId: string) => void;
  /** Notify the owning scroll container that a block changed height outside the parent commit. */
  onLayoutShift?: () => void;
}

export function BlockRouter({
  block,
  streaming,
  partialJson,
  timing,
  getToolResult,
  getSubagent,
  getActiveTask,
  getToolPending,
  sessionId,
  onStopTask,
  onLayoutShift,
}: BlockRouterProps) {
  switch (block.block_type) {
    case 'TextBlock':
      return <TextBlockView block={block} streaming={streaming} />;
    case 'ImageBlock':
      return <ImageBlockView block={block} />;
    case 'DocumentBlock':
      return <DocumentBlockView block={block} />;
    case 'ThinkingBlock':
      return <ThinkingBlockView block={block} timing={timing} streaming={streaming} onLayoutShift={onLayoutShift} />;
    case 'RedactedThinkingBlock':
      // Redacted thinking has no body to expand and no duration we
      // can show — render the bare label so the row still exists in
      // the narration without an affordance the user can't act on.
      return (
        <div className="text-[14px]">
          <span className="font-medium text-gray-700 dark:text-gray-200">Thought</span>{' '}
          <span className="text-gray-400 dark:text-gray-500">(redacted)</span>
        </div>
      );
    case 'ToolUseBlock':
    case 'ServerToolUseBlock': {
      // Pass ``block`` by reference -- never allocate a new object here.
      // A fresh ``{ ...block }`` every render busts ``ToolUseBlockView``'s
      // ``React.memo`` for every tool row on every SSE delta. The view
      // normalizes ``block_type`` internally (memoized on the block ref).
      return (
        <ToolUseBlockView
          block={block}
          result={getToolResult?.(block.id)}
          subagent={getSubagent?.(block.id)}
          activeTask={getActiveTask?.(block.id)}
          sessionId={sessionId}
          onStopTask={onStopTask}
          pending={getToolPending?.(block.id)}
          partialJson={partialJson}
          onLayoutShift={onLayoutShift}
        />
      );
    }
    case 'WebSearchToolResultBlock':
      return <WebSearchResultView block={block} />;
    case 'CodeExecutionToolResultBlock':
      return <CodeExecutionResultView block={block} />;
    case 'ToolResultBlock':
      // Tool results render inside their paired ToolUseBlock card, not standalone.
      return null;
    default:
      return null;
  }
}
